import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { useAuth } from '../../hooks/useAuth'
import {
  vehicleFleetCancelReservation,
  vehicleFleetCreateReservation,
  vehicleFleetGetAccess,
  vehicleFleetLoadReservations,
  vehicleFleetLoadVehicles,
  type VehicleFleetReservation,
  type VehicleFleetVehicle,
} from '../../lib/apiPhpAdapter'
import { VehicleFleetCalendarPanel } from './VehicleFleetCalendarPanel'

type Props = {
  showAlert: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

const DOW_LABELS: { id: number; label: string }[] = [
  { id: 1, label: 'Po' },
  { id: 2, label: 'Út' },
  { id: 3, label: 'St' },
  { id: 4, label: 'Čt' },
  { id: 5, label: 'Pá' },
  { id: 6, label: 'So' },
  { id: 7, label: 'Ne' },
]

function ymd(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function formatRange(starts: string, ends: string): string {
  const a = starts.replace('T', ' ').slice(0, 16)
  const b = ends.replace('T', ' ').slice(0, 16)
  return `${a} → ${b}`
}

function eachDateInclusive(from: string, to: string): string[] {
  const out: string[] = []
  const a = new Date(from + 'T12:00:00')
  const b = new Date(to + 'T12:00:00')
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) return out
  for (let d = new Date(a); d <= b; d.setDate(d.getDate() + 1)) {
    out.push(ymd(d))
  }
  return out
}

const inputStyle: CSSProperties = {
  padding: '0.5rem 0.6rem',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  fontSize: '0.95rem',
}

export function VehicleFleetDriverPanel({ showAlert }: Props) {
  const { user } = useAuth()
  const today = ymd(new Date())
  const [vehicles, setVehicles] = useState<VehicleFleetVehicle[]>([])
  const [reservations, setReservations] = useState<VehicleFleetReservation[]>([])
  const [vehicleId, setVehicleId] = useState('')
  const [mode, setMode] = useState<'period' | 'repeat'>('period')
  const [dateFrom, setDateFrom] = useState(today)
  const [dateTo, setDateTo] = useState(today)
  const [timeFrom, setTimeFrom] = useState('08:00')
  const [timeTo, setTimeTo] = useState('16:00')
  const [weekdays, setWeekdays] = useState<number[]>([1, 2, 3, 4, 5])
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [canManage, setCanManage] = useState(false)
  const [canApprove, setCanApprove] = useState(false)

  const periodPreview = useMemo(() => {
    if (mode !== 'period') return null
    if (!dateFrom || !dateTo || dateTo < dateFrom) return null
    return { starts: `${dateFrom} ${timeFrom}`, ends: `${dateTo} ${timeTo}` }
  }, [mode, dateFrom, dateTo, timeFrom, timeTo])

  const repeatDates = useMemo(() => {
    if (mode !== 'repeat') return []
    return eachDateInclusive(dateFrom, dateTo).filter((day) => {
      const d = new Date(day + 'T12:00:00')
      const isoDow = ((d.getDay() + 6) % 7) + 1
      return weekdays.includes(isoDow)
    })
  }, [mode, dateFrom, dateTo, weekdays])

  const myUserId = user?.id ? String(user.id) : ''

  const sortedVehicles = useMemo(() => {
    const mine: VehicleFleetVehicle[] = []
    const other: VehicleFleetVehicle[] = []
    for (const v of vehicles) {
      if (myUserId && v.assigned_driver_user_id && String(v.assigned_driver_user_id) === myUserId) {
        mine.push(v)
      } else {
        other.push(v)
      }
    }
    return [...mine, ...other]
  }, [vehicles, myUserId])

  const reload = async () => {
    const [vList, rList] = await Promise.all([
      vehicleFleetLoadVehicles(true),
      vehicleFleetLoadReservations({ all: true }),
    ])
    setVehicles(Array.isArray(vList) ? vList.filter((v) => !!v.active) : [])
    setReservations(Array.isArray(rList) ? rList : [])
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const [access] = await Promise.all([vehicleFleetGetAccess(), reload()])
        if (!cancelled) {
          setCanManage(!!(access.canFleetAdmin || access.isItAdmin || access.canAdmin))
          setCanApprove(!!(access.canApproveReservations || access.isDirector || access.isItAdmin || access.canAdmin))
        }
      } catch (e: unknown) {
        if (!cancelled) {
          showAlert(e instanceof Error ? e.message : 'Nepodařilo se načíst data', 'error')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [showAlert])

  // když uživatel posune „od“, a „do“ je dřív — srovnej
  useEffect(() => {
    if (dateTo < dateFrom) setDateTo(dateFrom)
  }, [dateFrom, dateTo])

  const activeMine = useMemo(
    () =>
      reservations.filter(
        (r) =>
          (r.status === 'active' || r.status === 'pending') &&
          (!user?.id || String(r.user_id) === String(user.id)),
      ),
    [reservations, user?.id],
  )

  const toggleDow = (id: number) => {
    setWeekdays((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id].sort()))
  }

  const submit = async () => {
    if (!vehicleId) {
      showAlert('Vyberte vozidlo', 'warning')
      return
    }
    if (dateTo < dateFrom) {
      showAlert('Datum „do“ musí být stejné nebo pozdější než „od“', 'error')
      return
    }
    if (mode === 'period' && dateFrom === dateTo && timeFrom >= timeTo) {
      showAlert('Čas „do“ musí být po „od“ (u rezervace v jednom dni)', 'error')
      return
    }
    if (mode === 'repeat' && timeFrom >= timeTo) {
      showAlert('Čas „do“ musí být po „od“', 'error')
      return
    }

    setBusy(true)
    try {
      if (mode === 'period') {
        const created = await vehicleFleetCreateReservation({
          vehicle_id: Number(vehicleId),
          starts_at: `${dateFrom}T${timeFrom}`,
          ends_at: `${dateTo}T${timeTo}`,
          note: note.trim() || undefined,
        })
        await reload()
        showAlert(
          created.status === 'pending'
            ? 'Žádost o rezervaci odeslána — čeká na schválení'
            : 'Rezervace vytvořena',
          'success',
        )
        return
      }

      if (repeatDates.length === 0) {
        showAlert('V zvoleném období není žádný vybraný den v týdnu', 'warning')
        return
      }
      let ok = 0
      let pendingCount = 0
      const errors: string[] = []
      for (const day of repeatDates) {
        try {
          const created = await vehicleFleetCreateReservation({
            vehicle_id: Number(vehicleId),
            starts_at: `${day}T${timeFrom}`,
            ends_at: `${day}T${timeTo}`,
            note: note.trim() || undefined,
          })
          ok++
          if (created.status === 'pending') pendingCount++
        } catch (e: unknown) {
          errors.push(`${day}: ${e instanceof Error ? e.message : 'chyba'}`)
        }
      }
      await reload()
      const okMsg =
        pendingCount > 0 && pendingCount === ok
          ? `Odesláno žádostí ke schválení: ${ok}`
          : pendingCount > 0
            ? `Vytvořeno ${ok} (z toho žádostí: ${pendingCount})`
            : `Vytvořeno rezervací: ${ok}`
      if (ok > 0 && errors.length === 0) showAlert(okMsg, 'success')
      else if (ok > 0) showAlert(`${okMsg}, chyby: ${errors.slice(0, 3).join('; ')}`, 'warning')
      else showAlert(errors[0] || 'Rezervace selhala', 'error')
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Rezervace selhala', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 960 }}>
      <section
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: '1.25rem 1.4rem',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
        }}
      >
        <h2 style={{ marginTop: 0, marginBottom: 6 }}>Nová rezervace</h2>
        <p style={{ color: '#64748b', margin: '0 0 14px', lineHeight: 1.45 }}>
          Nejčastěji: vyberte vozidlo a období <strong>od data do data</strong>. Volitelně můžete
          stejný čas opakovat ve vybrané dny. U <strong>vašeho</strong> stálého vozidla platí
          rezervace hned. U cizího vozidla vznikne <strong>žádost</strong> — schvaluje ji
          ředitel (admin autoparku žádost vidí, ale neschvaluje).
        </p>

        {loading ? (
          <p>Načítání…</p>
        ) : (
          <div style={{ display: 'grid', gap: 12, maxWidth: 560 }}>
            <label style={{ display: 'grid', gap: 4, fontSize: '0.9rem', fontWeight: 600 }}>
              1. Vozidlo
              <select
                value={vehicleId}
                disabled={busy}
                onChange={(e) => setVehicleId(e.target.value)}
                style={{ ...inputStyle, fontWeight: 400 }}
              >
                <option value="">— Vyberte vozidlo —</option>
                {sortedVehicles.map((v) => {
                  const isMine =
                    !!myUserId &&
                    !!v.assigned_driver_user_id &&
                    String(v.assigned_driver_user_id) === myUserId
                  const assigned =
                    !isMine && v.assigned_driver_name
                      ? ` · stálý: ${v.assigned_driver_name}`
                      : ''
                  return (
                    <option key={v.id} value={String(v.id)}>
                      {isMine ? '★ ' : ''}
                      {v.name} · {v.model} · {v.plate_number}
                      {isMine ? ' (vaše vozidlo)' : assigned}
                    </option>
                  )
                })}
              </select>
            </label>

            <div>
              <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 6 }}>2. Typ rezervace</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className={mode === 'period' ? 'system-button' : undefined}
                  disabled={busy}
                  onClick={() => setMode('period')}
                >
                  Od data do data
                </button>
                <button
                  type="button"
                  className={mode === 'repeat' ? 'system-button' : undefined}
                  disabled={busy}
                  onClick={() => setMode('repeat')}
                >
                  Opakovat ve dny
                </button>
              </div>
              <p style={{ margin: '8px 0 0', fontSize: '0.85rem', color: '#64748b', lineHeight: 1.4 }}>
                {mode === 'period'
                  ? 'Jedna souvislá rezervace (např. pondělí 8:00 → pátek 16:00).'
                  : 'Stejný čas každý vybraný den v rozmezí dat (např. Po–Pá 8:00–16:00).'}
              </p>
            </div>

            <div>
              <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 6 }}>3. Období</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <label style={{ display: 'grid', gap: 4, fontSize: '0.85rem' }}>
                  Od data
                  <input
                    type="date"
                    value={dateFrom}
                    disabled={busy}
                    onChange={(e) => setDateFrom(e.target.value)}
                    style={inputStyle}
                  />
                </label>
                <label style={{ display: 'grid', gap: 4, fontSize: '0.85rem' }}>
                  Do data
                  <input
                    type="date"
                    value={dateTo}
                    disabled={busy}
                    min={dateFrom}
                    onChange={(e) => setDateTo(e.target.value)}
                    style={inputStyle}
                  />
                </label>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                <label style={{ display: 'grid', gap: 4, fontSize: '0.85rem' }}>
                  {mode === 'period' ? 'Čas začátku' : 'Od (čas)'}
                  <input
                    type="time"
                    value={timeFrom}
                    disabled={busy}
                    onChange={(e) => setTimeFrom(e.target.value)}
                    style={inputStyle}
                  />
                </label>
                <label style={{ display: 'grid', gap: 4, fontSize: '0.85rem' }}>
                  {mode === 'period' ? 'Čas konce' : 'Do (čas)'}
                  <input
                    type="time"
                    value={timeTo}
                    disabled={busy}
                    onChange={(e) => setTimeTo(e.target.value)}
                    style={inputStyle}
                  />
                </label>
              </div>
            </div>

            {mode === 'repeat' ? (
              <div>
                <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 6 }}>Dny v týdnu</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {DOW_LABELS.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      disabled={busy}
                      className={weekdays.includes(d.id) ? 'system-button' : undefined}
                      onClick={() => toggleDow(d.id)}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div
              style={{
                padding: '0.65rem 0.75rem',
                borderRadius: 8,
                background: '#f0fdfa',
                border: '1px solid #99f6e4',
                fontSize: '0.9rem',
                color: '#134e4a',
              }}
            >
              {mode === 'period' && periodPreview ? (
                <>
                  Vytvoří se <strong>1 rezervace</strong>: {periodPreview.starts} → {periodPreview.ends}
                </>
              ) : mode === 'repeat' ? (
                <>
                  Vytvoří se <strong>{repeatDates.length}</strong> rezervací (každý den {timeFrom}–
                  {timeTo})
                </>
              ) : (
                'Zkontrolujte data.'
              )}
            </div>

            <label style={{ display: 'grid', gap: 4, fontSize: '0.9rem' }}>
              Poznámka (volitelně)
              <input
                value={note}
                disabled={busy}
                onChange={(e) => setNote(e.target.value)}
                placeholder="např. služební cesta do Plzně"
                style={inputStyle}
              />
            </label>

            <button
              type="button"
              className="system-button"
              disabled={
                busy ||
                !vehicleId ||
                (mode === 'period' && !periodPreview) ||
                (mode === 'repeat' && repeatDates.length === 0)
              }
              onClick={() => void submit()}
              style={{ justifySelf: 'start', padding: '0.55rem 1.1rem' }}
            >
              {(() => {
                const selected = vehicles.find((v) => String(v.id) === vehicleId)
                const isMine =
                  !!myUserId &&
                  !!selected?.assigned_driver_user_id &&
                  String(selected.assigned_driver_user_id) === myUserId
                return isMine || canManage ? 'Rezervovat' : 'Odeslat žádost'
              })()}
            </button>
          </div>
        )}
      </section>

      <VehicleFleetCalendarPanel
        showAlert={showAlert}
        timeFrom={timeFrom}
        timeTo={timeTo}
        canManage={canManage}
        canApprove={canApprove}
      />

      <section
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: '1.25rem 1.4rem',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
        }}
      >
        <h2 style={{ marginTop: 0 }}>Moje rezervace a žádosti</h2>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {activeMine.map((r) => (
            <li
              key={r.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                alignItems: 'flex-start',
                padding: '0.65rem 0',
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              <div>
                <strong>
                  {r.vehicle_name} · {r.plate_number}
                </strong>
                {r.status === 'pending' ? (
                  <div style={{ fontSize: '0.8rem', color: '#b45309', fontWeight: 600 }}>
                    Čeká na schválení
                  </div>
                ) : null}
                <div style={{ fontSize: '0.9rem', color: '#475569' }}>
                  {formatRange(r.starts_at, r.ends_at)}
                  {r.note ? (
                    <>
                      <br />
                      <span style={{ color: '#64748b' }}>{r.note}</span>
                    </>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  if (!window.confirm('Zrušit tuto rezervaci?')) return
                  void (async () => {
                    setBusy(true)
                    try {
                      await vehicleFleetCancelReservation(r.id)
                      await reload()
                      showAlert('Rezervace zrušena', 'success')
                    } catch (e: unknown) {
                      showAlert(e instanceof Error ? e.message : 'Zrušení selhalo', 'error')
                    } finally {
                      setBusy(false)
                    }
                  })()
                }}
              >
                Zrušit
              </button>
            </li>
          ))}
          {!loading && activeMine.length === 0 ? (
            <li style={{ color: '#64748b', padding: '0.5rem 0' }}>Žádné aktivní rezervace.</li>
          ) : null}
        </ul>
      </section>
    </div>
  )
}
